import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-toastify";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { IoCloseSharp } from "react-icons/io5";
import { BiSolidError } from "react-icons/bi";
import { RiDeleteBin6Fill } from "react-icons/ri";
import AdminPagination from "../../../component/pagination/AdminPagination";
import SkeletonLoading from "../../../component/SkeletonLoading";
import {
  useGetPaymentHistoryQuery,
  useRetryPaymentMutation,
  useDeleteParentPaymentMutation,
  useSubscribeNowMutation,
  useInitiateCardUpdateMutation,
  useResubscribeMutation,
} from "../../../store/api/school/PaymentHistorySlice";
import { useCancelPaymentPlanMutation } from "../../../store/api/admin/paymentSlice";
import { useParentPaymentMutation } from "../../../store/api/parent/registrationSlice";
import {
  capitalizeFirstLetter,
  formatDateToLocal,
} from "../../../utils/dateAndTimeConversion";
const initialPagination = { limit: 10, page: 1, total: "", totalPages: "" };

const statusClass = (status) => {
  if (!status) return "status unavailable";
  const s = (status || "").toLowerCase();
  if (s === "completed" || s === "paid") return "status bg-success text-white";
  if (s === "cancelled" || s === "rejected")
    return "status bg-danger text-white";
  if (s === "partial") return "status bg-warning text-dark";
  return "status unavailable";
};

const paymentTypeLabel = (type, t) => {
  if (type === "full_payment") return t ? t("payment_type_full") : "Full";
  if (type === "partial_payment") return t ? t("payment_type_partial") : "Partial";
  if (type === "student_fee") return t ? t("payment_type_student_fee") : "Student Fee";
  if (type === "deposit") return t ? t("payment_type_deposit") : "Deposit";
  return type || "—";
};

const ParentPaymentHistory = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.UserReducer);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(initialPagination);
  const [retryingId, setRetryingId] = useState(null);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const [retryPayment] = useRetryPaymentMutation();
  const [deleteParentPayment, { isLoading: isDeleting }] =
    useDeleteParentPaymentMutation();
  const [cancelPaymentPlan, { isLoading: isCancelling }] =
    useCancelPaymentPlanMutation();
  const [subscribeNow, { isLoading: isSubscribingNow }] =
    useSubscribeNowMutation();
  const [initiateCardUpdate, { isLoading: isUpdatingCard }] =
    useInitiateCardUpdateMutation();
  const [resubscribe, { isLoading: isResubscribing }] =
    useResubscribeMutation();
  const [parentPayment, { isLoading: isPayingBalance }] =
    useParentPaymentMutation();

  const [payBalanceModal, setPayBalanceModal] = useState(false);
  const [balancePayType, setBalancePayType] = useState("full_payment");

  const handlePayBalance = async () => {
    try {
      const dueAmount = parseFloat(summary?.due_amount || 0);
      const installmentAmount = (dueAmount / (summary?.remaining_installments || 3)).toFixed(2);
      const payload = {
        currency: history[0]?.currency || "USD",
        paymentType: balancePayType,
        ...(balancePayType === "partial_payment" && { partialAmount: parseFloat(installmentAmount) }),
      };
      const response = await parentPayment(payload).unwrap();
      if (response?.data?.iframe_url) {
        window.open(response.data.iframe_url, "_blank");
        setPayBalanceModal(false);
        toast.success(t("payment_open_tab"));
      } else {
        toast.error(t("payment_link_error"));
      }
    } catch (err) {
      toast.error(err?.data?.message || t("payment_failed_retry"));
    }
  };

  const handleDelete = async () => {
    try {
      await deleteParentPayment(deleteTargetId).unwrap();
      toast.success(t("payment_record_deleted"));
      setDeleteTargetId(null);
    } catch (err) {
      toast.error(err?.data?.message || t("delete_payment_failed"));
      setDeleteTargetId(null);
    }
  };

  const handleRetry = async (orderId) => {
    toast.dismiss();
    setRetryingId(orderId);
    try {
      const response = await retryPayment(orderId).unwrap();
      if (response?.data?.already_completed) {
        toast.success(t("payment_already_processed"));
      } else if (response?.data?.iframe_url) {
        window.open(response.data.iframe_url, "_blank");
      } else {
        toast.error(t("payment_link_error"));
      }
    } catch (err) {
      toast.error(err?.data?.message || t("retry_failed"));
    } finally {
      setRetryingId(null);
    }
  };

  const { data, isSuccess, isLoading, isFetching, refetch } =
    useGetPaymentHistoryQuery({
      page: pagination.page,
      limit: pagination.limit,
    });

  const handleCancelSubscription = async () => {
    if (!cancelReason.trim()) {
      toast.error(t("provide_cancellation_reason"));
      return;
    }
    const userId = user?.id || user?._id;
    try {
      await cancelPaymentPlan({
        id: userId,
        data: { reason: cancelReason.trim() },
      }).unwrap();
      toast.success(t("subscription_cancelled_success"));
      setCancelModalOpen(false);
      setCancelReason("");
      // cancelPaymentPlan lives in a separate RTK Query API instance
      // (paymentSlice) from useGetPaymentHistoryQuery (paymentHistorySlice) —
      // its invalidatesTags can't refresh this table, so refetch directly.
      refetch();
    } catch (err) {
      toast.error(
        err?.data?.message || t("cancel_subscription_failed"),
      );
    }
  };

  const handleSubscribeNow = async () => {
    try {
      const response = await subscribeNow().unwrap();
      toast.success(response?.message || t("subscribed_next_year"));
    } catch (err) {
      toast.error(err?.data?.message || t("subscribe_now_error"));
    }
  };

  const handleUpdatePaymentMethod = async () => {
    try {
      const response = await initiateCardUpdate().unwrap();
      const iframeUrl = response?.data?.iframe_url;
      if (iframeUrl) {
        window.open(iframeUrl, "_blank");
        toast.success(t("card_verify_tab"));
      } else {
        toast.error(t("card_verify_error"));
      }
    } catch (err) {
      toast.error(err?.data?.message || t("card_verify_error"));
    }
  };

  const handleResubscribe = async () => {
    try {
      const response = await resubscribe().unwrap();
      toast.success(response?.message || t("resubscribed_success"));
    } catch (err) {
      toast.error(err?.data?.message || t("resubscribe_error"));
    }
  };

  useEffect(() => {
    if (isSuccess && data?.data) {
      setHistory(data.data.history || []);
      setSummary(data.data.summary || null);
      setPagination((prev) => ({
        ...prev,
        total: data.data.pagination.total,
        page: data.data.pagination.page,
        limit: data.data.pagination.limit,
        totalPages: data.data.pagination.pages,
      }));
    }
  }, [data, isSuccess]);

  const handlePageChange = (page) =>
    setPagination((prev) => ({ ...prev, page }));
  const handleLimitChange = (limit) =>
    setPagination((prev) => ({ ...prev, limit, page: 1 }));

  const currSym = (c) => (c === "CAD" ? "C$" : "$");
  const summaryCurrency = history[0]?.currency || "USD";
  const cycleEnded =
    !!summary?.is_active &&
    !!summary?.academic_year_end &&
    new Date(summary.academic_year_end) <= new Date();

  return (
    <>
      {/* Academic Year Banner */}
      {summary?.academic_year_start && (
        <div className="academicYearBanner mb-4">
          {summary.payment_error && (
            <div className="academicYearError mb-2">
              ⚠ {t("payment_declined_warning")}
            </div>
          )}
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div>
              <div className="academicYearTitle">
                {t("subscribed_from_to", { startDate: formatDateToLocal(summary.academic_year_start), endDate: formatDateToLocal(summary.academic_year_end) })}
              </div>
              <div className="academicYearSub">
                {paymentTypeLabel(summary.current_payment_type, t)} {t("payment")}
                {summary.current_payment_type !== "deposit" && summary.remaining_installments > 0 && (
                  <> · {summary.remaining_installments} {t("installments")} {t("remaining")}</>
                )}
                {summary.current_payment_type === "deposit" && parseFloat(summary.due_amount || 0) > 0 && (
                  <> · {t("spot_reserved")}</>
                )}
              </div>
              {summary.is_active && summary.next_renewal_date && (
                <div className="academicYearRenew">
                  {t("auto_renews_on", {
                    date: formatDateToLocal(summary.next_renewal_date),
                    startYear: new Date(summary.next_renewal_date).getFullYear(),
                    endYear: new Date(summary.next_renewal_date).getFullYear() + 1,
                  })}
                </div>
              )}
              {cycleEnded && (
                <div className="academicYearRenew">
                  {t("subscribe_now_desc")}
                </div>
              )}
              {summary.is_cancelled && (
                <div className="academicYearRenew">
                  {t("subscription_cancelled_msg")}
                </div>
              )}
            </div>
            {summary.is_cancelled ? (
              <div className="d-flex gap-2">
                <Button
                  className="smallBtn auto"
                  onClick={handleResubscribe}
                  disabled={isResubscribing}
                >
                  {isResubscribing ? (
                    <span className="spinner-border spinner-border-sm" role="status" />
                  ) : (
                    t("resubscribe")
                  )}
                </Button>
              </div>
            ) : (
              summary.is_active && (
                <div className="d-flex gap-2 flex-wrap">
                  {cycleEnded && (
                    <Button
                      className="smallBtn auto"
                      onClick={handleSubscribeNow}
                      disabled={isSubscribingNow}
                    >
                      {isSubscribingNow ? (
                        <span className="spinner-border spinner-border-sm" role="status" />
                      ) : (
                        t("subscribe_now")
                      )}
                    </Button>
                  )}
                  {parseFloat(summary.due_amount || 0) > 0 && (
                    <Button
                      className="smallBtn auto"
                      onClick={() => setPayBalanceModal(true)}
                    >
                      {t("pay_outstanding_balance")}
                    </Button>
                  )}
                  {Number(summary.total_amount) > 0 && (
                    <Button
                      className="smallBtn"
                      onClick={handleUpdatePaymentMethod}
                      disabled={isUpdatingCard}
                    >
                      {isUpdatingCard ? (
                        <span className="spinner-border spinner-border-sm" role="status" />
                      ) : (
                        t("update_payment_method")
                      )}
                    </Button>
                  )}
                  <Button
                    className="smallBtn cancel"
                    onClick={() => setCancelModalOpen(true)}
                  >
                    {t("cancel_subscription")}
                  </Button>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="row g-3 mb-4">
          <div className="col-sm-6 col-lg-3">
            <div className="summaryCard">
              <div className="summaryLabel">
                {t("total")} {t("amount")}
              </div>
              <div className="summaryValue">
                {currSym(summaryCurrency)}{parseFloat(summary.total_amount || 0).toFixed(2)}
              </div>
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="summaryCard summaryCardSuccess">
              <div className="summaryLabel">
                {t("paid")} {t("amount")}
              </div>
              <div className="summaryValue">
                {currSym(summaryCurrency)}{parseFloat(summary.paid_amount || 0).toFixed(2)}
              </div>
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="summaryCard summaryCardDanger">
              <div className="summaryLabel">
                {t("outstanding")} {t("amount")}
              </div>
              <div className="summaryValue">
                {currSym(summaryCurrency)}{parseFloat(summary.due_amount || 0).toFixed(2)}
              </div>
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="summaryCard">
              <div className="summaryLabel">
                {t("payment")} {t("status")}
              </div>
              <div className="summaryValue">
                <span className={statusClass(summary.payment_status)}>
                  {summary?.current_payment_type === "deposit" && summary?.payment_status === "partial"
                    ? "Deposit"
                    : summary?.payment_status && capitalizeFirstLetter(summary.payment_status)}
                </span>
              </div>
              {summary.current_payment_type !== "deposit" && summary.remaining_installments > 0 && (
                <div className="summaryNote">
                  {summary.remaining_installments} {t("installments")}{" "}
                  {t("remaining")}
                </div>
              )}
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="summaryCard">
              <div className="summaryLabel">
                {t("last")} {t("payment")} {t("date")}
              </div>
              <div className="summaryValue summaryValueSm">
                {summary.last_payment_date
                  ? formatDateToLocal(summary.last_payment_date)
                  : "—"}
              </div>
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="summaryCard summaryCardWarning">
              <div className="summaryLabel">
                {t("next")} {t("payment")} {t("due")}
              </div>
              <div className="summaryValue summaryValueSm">
                {summary.next_payment_due
                  ? formatDateToLocal(summary.next_payment_due)
                  : "—"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Total count */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="dbSubHd">
          {t("total")}: {pagination.total}
        </div>
      </div>

      {/* Transactions Table */}
      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th>
                {t("Sr")}.{t("no")}.
              </th>
              <th>
                {t("order")} {t("number")}
              </th>
              <th>{t("amount")}</th>
              <th>{t("currency")}</th>
              <th className="text-nowrap">
                {t("payment")} {t("type")}
              </th>
              <th>
                {t("transaction")} {t("id")}
              </th>

              <th className="text-nowrap">
                {t("created")} {t("by")}
              </th>
              <th className="text-nowrap">
                {t("paid")} {t("to")}
              </th>
              <th>{t("date")}</th>
              <th className="text-nowrap">
                {t("payment")} {t("status")}
              </th>
              <th>{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              <SkeletonLoading MainLength={10} length={11} />
            ) : history?.length > 0 ? (
              history.map((item, i) => (
                <tr key={item.id}>
                  <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                  <td className="text-nowrap">{item.order_number || "—"}</td>
                  <td className="text-nowrap">
                    {currSym(item.currency)}{(item.amount_cents / 100).toFixed(2)}
                  </td>
                  <td className="text-nowrap">{item.currency}</td>
                  <td className="text-nowrap">
                    {paymentTypeLabel(item.payment_type, t)}
                  </td>
                  <td className="text-nowrap">{item.transaction_id || "—"}</td>

                  {/* Created By */}
                  <td className="text-nowrap">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <strong>{item.creator?.name || "—"}</strong>
                      <small
                        style={{
                          fontSize: 11,
                          color: "#94a3b8",
                          textTransform: "capitalize",
                        }}
                      >
                        {item.creator?.role}
                      </small>
                    </div>
                  </td>

                  <td className="text-nowrap">
                    {item.effective_bank ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <span>{item.effective_bank.bank_name || "—"}</span>
                        {item.effective_bank.routed_through_admin && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              padding: "1px 6px",
                              borderRadius: 10,
                              background: "#fde8c8",
                              color: "#c8900a",
                              border: "1px solid #f0b342",
                              width: "fit-content",
                            }}
                          >
                            {t("via")} {t("admin")}
                          </span>
                        )}
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="text-nowrap">
                    {formatDateToLocal(item.created_at)}
                  </td>
                  <td className="text-nowrap">
                    <span className={statusClass(item.payment_status)}>
                      {item.payment_status}
                    </span>
                  </td>
                  <td className="text-nowrap">
                    <div className="d-flex gap-1 align-items-center">
                      {/* {item.payment_status?.toLowerCase() === "created" && (
                        <button
                          className="btn btn-warning btn-sm text-white"
                          onClick={() => handleRetry(item.id)}
                          disabled={retryingId === item.id}
                        >
                          {retryingId === item.id ? (
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          ) : (
                            t("retry") || "Retry"
                          )}
                        </button>
                      )} */}
                      {item.payment_status?.toLowerCase() !== "completed" && (
                        <Button
                          className="rounded-circle"
                          onClick={() => setDeleteTargetId(item.id)}
                          title={t("delete")}
                        >
                          <RiDeleteBin6Fill />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr className="text-center">
                <td colSpan="11">
                  {t("no")} {t("details")} {t("found")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={!!deleteTargetId} centered>
        <Button className="closePopup" onClick={() => setDeleteTargetId(null)}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div className="text-center mb-3 errorIcon">
            <BiSolidError />
          </div>
          <div className="text-center mb-4 deleteHd">
            {`${t("are")} ${t("you")} ${t("sure")} ${t("want")} ${t("to")} ${t("delete")} ${t("this")} ${t("payment")} ${t("record")}?`}
          </div>
          <div className="text-center">
            <Button
              className="smallBtn cancel me-3"
              onClick={() => setDeleteTargetId(null)}
            >
              {t("btns.cancel")}
            </Button>
            <Button
              className="smallBtn auto"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                />
              ) : (
                t("btns.delete")
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Cancel Subscription Modal */}
      <Modal
        show={cancelModalOpen}
        centered
        onHide={() => {
          setCancelModalOpen(false);
          setCancelReason("");
        }}
      >
        <Button
          className="closePopup"
          onClick={() => {
            setCancelModalOpen(false);
            setCancelReason("");
          }}
        >
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div className="text-center mb-3 errorIcon">
            <BiSolidError />
          </div>
          <div className="text-center mb-3 deleteHd">
            {t("subscription_cancel_modal")}
          </div>
          <textarea
            className="form-control mb-4"
            rows={3}
            placeholder={t("cancellation_reason_placeholder")}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
          <div className="text-center">
            <Button
              className="smallBtn cancel me-3"
              onClick={() => {
                setCancelModalOpen(false);
                setCancelReason("");
              }}
            >
              {t("btns.cancel")}
            </Button>
            <Button
              className="smallBtn auto"
              onClick={handleCancelSubscription}
              disabled={isCancelling}
            >
              {isCancelling ? (
                <span className="spinner-border spinner-border-sm" role="status" />
              ) : (
                t("cancel_subscription")
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Pay Outstanding Balance Modal */}
      <Modal show={payBalanceModal} centered onHide={() => setPayBalanceModal(false)}>
        <Button className="closePopup" onClick={() => setPayBalanceModal(false)}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div className="text-center mb-3 deleteHd">{t("pay_outstanding_balance")}</div>
          <div className="text-center mb-4" style={{ color: "#6b7280", fontSize: "0.95rem" }}>
            {t("remaining")}: {currSym(summaryCurrency)}
            {parseFloat(summary?.due_amount || 0).toFixed(2)}
          </div>

          <div className="d-flex flex-column gap-3 mb-4">
            <div
              onClick={() => setBalancePayType("full_payment")}
              style={{
                border: `2px solid ${balancePayType === "full_payment" ? "#f0b241" : "#e5e7eb"}`,
                borderRadius: 10,
                padding: "14px 18px",
                cursor: "pointer",
                background: balancePayType === "full_payment" ? "rgba(240,178,65,0.08)" : "#fff",
              }}
            >
              <div style={{ fontWeight: 700 }}>{t("full_payment")}</div>
              <div style={{ color: "#6b7280", fontSize: "0.88rem" }}>
                {t("full_payment_balance_desc", { amount: `${currSym(summaryCurrency)}${parseFloat(summary?.due_amount || 0).toFixed(2)}` })}
              </div>
            </div>

            <div
              onClick={() => setBalancePayType("partial_payment")}
              style={{
                border: `2px solid ${balancePayType === "partial_payment" ? "#f0b241" : "#e5e7eb"}`,
                borderRadius: 10,
                padding: "14px 18px",
                cursor: "pointer",
                background: balancePayType === "partial_payment" ? "rgba(240,178,65,0.08)" : "#fff",
              }}
            >
              <div style={{ fontWeight: 700 }}>{summary?.remaining_installments || 3} Installments</div>
              <div style={{ color: "#6b7280", fontSize: "0.88rem" }}>
                {currSym(summaryCurrency)}
                {(parseFloat(summary?.due_amount || 0) / (summary?.remaining_installments || 3)).toFixed(2)}
                {" "}× {summary?.remaining_installments || 3} payments
              </div>
            </div>
          </div>

          <div className="text-center">
            <Button
              className="smallBtn cancel me-3"
              onClick={() => setPayBalanceModal(false)}
            >
              {t("btns.cancel")}
            </Button>
            <Button
              className="smallBtn auto"
              onClick={handlePayBalance}
              disabled={isPayingBalance}
            >
              {isPayingBalance ? (
                <span className="spinner-border spinner-border-sm" role="status" />
              ) : (
                t("proceed_to_payment")
              )}
            </Button>
          </div>
        </div>
      </Modal>

      <style>{`
        .summaryCard {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 16px 20px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }
        .summaryCardSuccess  { border-left: 4px solid #22c55e; }
        .summaryCardDanger   { border-left: 4px solid #ef4444; }
        .summaryCardWarning  { border-left: 4px solid #f0b241; }
        .summaryLabel {
          font-size: 0.78rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #6b7280;
          margin-bottom: 6px;
          font-weight: 600;
        }
        .summaryValue {
          font-size: 1.25rem;
          font-weight: 700;
          color: #111827;
        }
        .summaryValueSm {
          font-size: 0.95rem;
          font-weight: 600;
          color: #374151;
        }
        .summaryNote {
          font-size: 0.75rem;
          color: #9ca3af;
          margin-top: 4px;
        }
        .academicYearBanner {
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          border-left: 4px solid #0b3b6f;
          border-radius: 12px;
          padding: 16px 20px;
        }
        .academicYearTitle {
          font-size: 1.05rem;
          font-weight: 700;
          color: #0b3b6f;
        }
        .academicYearSub {
          font-size: 0.85rem;
          color: #4b5563;
          margin-top: 4px;
        }
        .academicYearRenew {
          font-size: 0.78rem;
          color: #6b7280;
          margin-top: 6px;
        }
        .academicYearError {
          font-size: 0.85rem;
          font-weight: 600;
          color: #991b1b;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 6px;
          padding: 8px 12px;
        }
      `}</style>
    </>
  );
};

export default ParentPaymentHistory;
